import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { LuInfo, LuSettings2 } from 'react-icons/lu';

import { BaseCard } from '@/components/BaseCard';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAssetsByIdsMap } from '@/hooks/market-data-queries';
import { useAccountGroupProfileWithAumIncluded } from '@/views/portfolio/account-aum-queries';
import { useGetPortfolioTradePreferences } from '@/views/portfolio/account-group-queries';
import { categoryIconMap, restrictionTypeMap } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/constants';
import { TAX_LOSS_HARVESTING } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/investment-styles/constants';
import {
    RestrictionsInfo,
    useRestrictionsData,
} from 'views/portfolio/PortfolioPage/tabs/equity-preferences/useRestrictionsData';

interface EquityPreferencesCardProps {
    accountGroupGUID: string;
}

export const EquityPreferencesCard = ({ accountGroupGUID }: EquityPreferencesCardProps) => {
    const { data: accountGroupProfile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);
    const { data: preferences } = useGetPortfolioTradePreferences(accountGroupGUID);

    const allRestrictionShareIds = useMemo(() => {
        const ids = new Set<string>();
        preferences?.restrictions?.forEach((r) => ids.add(r.shareId));
        preferences?.restrictedCategoryOverrides?.forEach((o) => ids.add(o.shareId));
        return Array.from(ids);
    }, [preferences?.restrictions, preferences?.restrictedCategoryOverrides]);

    const { data: assetsMap } = useAssetsByIdsMap(allRestrictionShareIds);

    const { restrictionsInfo: data } = useRestrictionsData(accountGroupGUID, assetsMap);
    const renderExceptions = (
        exceptions: Array<{ ticker: string; shareId: string; shareClassId?: string; company: string }>,
    ) => {
        if (exceptions.length === 0) return null;

        return (
            <>
                <div className="mb-2 text-xs text-gray-600">Exceptions:</div>
                <div className="flex flex-wrap gap-1">
                    {exceptions.map((exception) => (
                        <div key={exception.shareClassId || exception.shareId}>
                            <div className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span>{exception.ticker}</span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" align="center">
                                        {exception.company}
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderMainCategories = (sriRestrictions: RestrictionsInfo['sriRestrictions']) => {
        if (!sriRestrictions) return null;

        return (
            <div className="mb-2 flex flex-col gap-2 text-sm font-medium text-gray-800">
                {Object.keys(sriRestrictions).map((mainCategory) => {
                    const CategoryIcon = categoryIconMap[mainCategory as keyof typeof categoryIconMap] || LuInfo;
                    const subCategories = sriRestrictions[mainCategory];
                    const exceptions = subCategories
                        .flatMap((subCategory) => subCategory.exceptions)
                        .filter(
                            (exception, index, array) =>
                                array.findIndex((e) => e.shareClassId === exception.shareClassId) === index,
                        )
                        .sort((a, b) => a.ticker.localeCompare(b.ticker));
                    return (
                        <div key={mainCategory}>
                            <div className="flex items-center gap-2">
                                <CategoryIcon className="h-4 w-4 text-gray-600" />
                                <span>{subCategories[0].categoryName}</span>
                                <span className="ml-1 text-gray-600">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span>({subCategories.length} categories)</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" align="center">
                                            {subCategories.map((subCategory) => subCategory.subCategoryName).join(', ')}
                                        </TooltipContent>
                                    </Tooltip>
                                </span>
                            </div>
                            {renderExceptions(exceptions)}
                        </div>
                    );
                })}
            </div>
        );
    };

    const investmentStyles = preferences?.taxLossHarvesting ? [TAX_LOSS_HARVESTING.shortLabel] : [];

    return (
        <BaseCard
            title="Equity Preferences"
            icon={LuSettings2}
            headerClassName="bg-[#C6E6E6]"
            loading={data.isLoading}
            navigateUrl="equity-preferences"
        >
            {isEmpty(data.clientRestrictions) ? (
                <div className="mb-4 flex items-center gap-2 border-b border-gray-300 pb-4 text-sm font-medium text-gray-700">
                    <div className="text-sm font-medium text-gray-700">
                        Client Restrictions
                        <div className="mt-2 text-sm text-gray-500">None</div>
                    </div>
                </div>
            ) : (
                <div className="pb-2">
                    <div className="mb-2 text-sm font-medium text-gray-700">
                        Client Restrictions ({data.clientRestrictions.length} securities)
                    </div>

                    <div className="mt-5 flex flex-wrap gap-1 border-b border-gray-300 pb-4">
                        {data.clientRestrictions.map((security) => (
                            <Tooltip key={security.shareId}>
                                <TooltipTrigger asChild>
                                    <div className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                                        {security.ticker}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" align="center">
                                    {restrictionTypeMap[security.restrictionType]} {security.company}
                                </TooltipContent>
                            </Tooltip>
                        ))}
                    </div>
                </div>
            )}
            <div>
                <div className="mb-2 text-sm font-medium text-gray-700">SRI Restrictions</div>
                {isEmpty(data?.sriRestrictions) ? (
                    <div className="text-sm text-gray-500">None</div>
                ) : (
                    <div className="border-border rounded-lg border bg-gray-50 p-3">
                        {renderMainCategories(data.sriRestrictions)}
                    </div>
                )}
            </div>
            <div className="mt-4 mb-2 border-t border-gray-300 pt-2 text-sm font-medium text-gray-700">
                Investment Styles
            </div>
            {isEmpty(investmentStyles) ? (
                <div className="text-gray-500">None</div>
            ) : (
                investmentStyles.map((style: string) => (
                    <Badge key={style} className="mr-2 mb-2 bg-gray-200 text-black">
                        {style}
                    </Badge>
                ))
            )}
        </BaseCard>
    );
};
